import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Paperclip, X, Download, Users, User, Loader2, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Chat component for clinic internal messaging.
 * @param {string} apiBase - Base URL for chat API (e.g., "/api/chat" or "/api/portal/doctor/chat")
 * @param {function} getHeaders - Function returning auth headers
 * @param {string} currentUserId - Current user's ID
 * @param {string} currentUserName - Current user's display name
 */
export function ClinicChat({ apiBase, getHeaders, currentUserId, currentUserName }) {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeChat, setActiveChat] = useState(null); // null = group, string = private user id
  const [activeChatName, setActiveChatName] = useState('');
  const [showUsers, setShowUsers] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const headers = getHeaders();

  const fetchMessages = async (chatId = activeChat) => {
    try {
      const params = chatId ? `?recipient_id=${chatId}` : '';
      const res = await axios.get(`${API}${apiBase}/messages${params}`, { headers });
      setMessages(res.data || []);
    } catch (e) {
      if (e.response?.status === 403) setChatEnabled(false);
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}${apiBase}/users`, { headers });
      setUsers(res.data || []);
    } catch (e) { /* chat disabled */ }
  };

  useEffect(() => {
    fetchMessages();
    fetchUsers();
    // Mark as read
    axios.post(`${API}${apiBase}/mark-read`, {}, { headers }).catch(() => {});
    // Poll every 5s
    pollRef.current = setInterval(() => fetchMessages(), 5000);
    return () => clearInterval(pollRef.current);
  }, [activeChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}${apiBase}/messages`, {
        text: text.trim(),
        recipient_id: activeChat,
      }, { headers });
      setMessages(prev => [...prev, res.data]);
      setText('');
    } catch (e) { toast.error('Errore invio messaggio'); }
    setSending(false);
  };

  const sendFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('File troppo grande (max 10MB)');
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('text', '');
      if (activeChat) formData.append('recipient_id', activeChat);
      const res = await axios.post(`${API}${apiBase}/messages/file`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, res.data]);
    } catch (e) { toast.error('Errore invio file'); }
    setSending(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadFile = async (msgId, fileName) => {
    try {
      const res = await axios.get(`${API}${apiBase}/messages/${msgId}/download`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };

  const openPrivateChat = (userId, userName) => {
    setActiveChat(userId);
    setActiveChatName(userName);
    setShowUsers(false);
    setLoading(true);
  };

  const backToGroup = () => {
    setActiveChat(null);
    setActiveChatName('');
    setLoading(true);
  };

  if (!chatEnabled) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="chat-disabled">
        <MessageCircle className="w-10 h-10 text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">La chat non è attiva per questa clinica</p>
        <p className="text-slate-400 text-xs mt-1">Contatta il superadmin per attivarla</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="clinic-chat">
      {/* Chat header */}
      <div className="flex items-center gap-2 bg-white border-b border-slate-200 px-3 py-2">
        {activeChat ? (
          <>
            <button onClick={backToGroup} className="p-1 hover:bg-slate-100 rounded-lg" data-testid="chat-back">
              <ChevronLeft className="w-4 h-4 text-slate-500" />
            </button>
            <User className="w-4 h-4 text-sky-500" />
            <span className="font-medium text-slate-800 text-sm flex-1">{activeChatName}</span>
          </>
        ) : (
          <>
            <Users className="w-4 h-4 text-emerald-500" />
            <span className="font-medium text-slate-800 text-sm flex-1">Chat Clinica</span>
          </>
        )}
        <button onClick={() => setShowUsers(!showUsers)}
          className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded-lg transition-colors" data-testid="chat-users-btn">
          {showUsers ? 'Chiudi' : 'Messaggi privati'}
        </button>
      </div>

      {/* Users list for private chat */}
      {showUsers && (
        <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 space-y-1 max-h-40 overflow-y-auto">
          {users.filter(u => u.id !== currentUserId).map(u => (
            <button key={u.id} onClick={() => openPrivateChat(u.id, u.name)}
              className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white transition-colors" data-testid={`chat-user-${u.id}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${u.role === 'doctor' ? 'bg-emerald-500' : 'bg-sky-500'}`}>
                {(u.name || '?')[0].toUpperCase()}
              </div>
              <span className="text-sm text-slate-700">{u.name}</span>
              <span className="text-[10px] text-slate-400 ml-auto">{u.role === 'doctor' ? 'Medico' : u.role === 'receptionist' ? 'Receptionist' : u.role}</span>
            </button>
          ))}
          {users.filter(u => u.id !== currentUserId).length === 0 && (
            <p className="text-xs text-slate-400 text-center py-2">Nessun altro utente</p>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 bg-slate-50" style={{ minHeight: '200px', maxHeight: '400px' }}>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-slate-400 text-xs">{activeChat ? 'Nessun messaggio privato' : 'Nessun messaggio. Inizia la conversazione!'}</p>
          </div>
        ) : (
          messages.map(msg => {
            const isMe = msg.sender_id === currentUserId;
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 ${isMe ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200'}`}>
                  {!isMe && (
                    <p className={`text-[10px] font-semibold mb-0.5 ${isMe ? 'text-emerald-100' : 'text-emerald-600'}`}>
                      {msg.sender_name}
                    </p>
                  )}
                  {msg.text && <p className={`text-sm ${isMe ? 'text-white' : 'text-slate-700'}`}>{msg.text}</p>}
                  {msg.file && (
                    <button onClick={() => downloadFile(msg.id, msg.file.name)}
                      className={`flex items-center gap-1.5 mt-1 text-xs ${isMe ? 'text-emerald-100 hover:text-white' : 'text-sky-600 hover:text-sky-700'}`}>
                      <Download className="w-3 h-3" />
                      {msg.file.name} ({(msg.file.size / 1024).toFixed(0)} KB)
                    </button>
                  )}
                  <p className={`text-[9px] mt-0.5 ${isMe ? 'text-emerald-200' : 'text-slate-300'}`}>
                    {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white border-t border-slate-200 px-3 py-2 flex items-center gap-2">
        <input type="file" ref={fileInputRef} onChange={sendFile} className="hidden" data-testid="chat-file-input" />
        <button onClick={() => fileInputRef.current?.click()} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600" data-testid="chat-attach-btn">
          <Paperclip className="w-4 h-4" />
        </button>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Scrivi un messaggio..."
          className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          data-testid="chat-input"
        />
        <button onClick={sendMessage} disabled={sending || !text.trim()}
          className="p-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-lg transition-colors" data-testid="chat-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
